import { useEffect, useRef, useCallback, useState } from 'react';

import { WS_URL } from '../lib/backendUrl';
import { getLobbySocket } from '../lib/lobbySocket';

// Sync rate: 60ms = ~16 updates per second
const SYNC_INTERVAL = 60;

export const useMultiplayerSync = (lobbyId, walletAddress, isActive = false) => {
  const subRef = useRef(null);       // active subscriber handle from lobbySocket
  const syncIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [otherPlayers, setOtherPlayers] = useState({});
  const [syncedEnemies, setSyncedEnemies] = useState(null);
  const [syncedBullets, setSyncedBullets] = useState([]);
  const [syncedEnemyKills, setSyncedEnemyKills] = useState([]);
  const [syncedHostState, setSyncedHostState] = useState(null);
  const [syncedPowerUps, setSyncedPowerUps] = useState(null);
  const [pendingKillCredits, setPendingKillCredits] = useState([]);
  const [pendingPowerupPickupIds, setPendingPowerupPickupIds] = useState([]);
  const [pendingRevivals, setPendingRevivals] = useState([]);
  const [isHost, setIsHost] = useState(false);
  const localStateRef = useRef(null);
  const isHostRef = useRef(false);

  // (sendRaw helper removed — call sites use subRef.current.send directly.)
  
  // Subscribe to the shared lobby socket. The lobbySocket singleton owns the
  // underlying WebSocket lifecycle — see /app/frontend/src/lib/lobbySocket.js.
  // We just register a handler and `send()` through it; the same socket is
  // shared with MultiplayerLobby during the lobby phase, eliminating the
  // reconnect cycle that used to happen during the lobby → game handoff.
  useEffect(() => {
    if (!isActive) return;
    if (!lobbyId || !walletAddress) {
      console.warn('[MP] cannot connect: missing', {
        lobbyId: lobbyId || '(missing)',
        walletAddress: walletAddress || '(missing)',
      });
      return;
    }

    const sub = getLobbySocket(lobbyId, walletAddress, WS_URL);
    subRef.current = sub;

    const onMessage = (data) => {
      if (data.type === '__socket_open__') {
        console.log('[MP] WebSocket connected for game sync');
        setIsConnected(true);
        // Request initial game state from peers
        sub.send({ type: 'game_sync_request', data: { requestInitialState: true } });
        return;
      }
      if (data.type === '__socket_close__') {
        console.log('[MP] WebSocket disconnected');
        setIsConnected(false);
        return;
      }
      try { handleMessage(data); }
      catch (e) { console.error('[MP] handler error:', e); }
    };

    const unsub = sub.subscribe(onMessage);

    return () => {
      unsub();
      subRef.current = null;
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobbyId, walletAddress, isActive]);
  
  // Handle incoming messages
  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'player_state':
        // Update other player's state
        if (data.wallet_address !== walletAddress) {
          setOtherPlayers(prev => ({
            ...prev,
            [data.wallet_address]: {
              ...data.state,
              lastUpdate: Date.now()
            }
          }));
        }
        break;
        
      case 'game_action':
        // Handle game actions from other players
        if (data.wallet_address !== walletAddress) {
          handleGameAction(data.action, data.data, data.wallet_address);
        }
        break;
        
      case 'enemy_sync':
        // Host broadcasts enemy state + game-level mirror (bossActive, levelProgress, level)
        // and authoritative power-ups list.
        if (data.wallet_address !== walletAddress) {
          setSyncedEnemies(data.enemies);
          if (data.host_state) {
            setSyncedHostState(data.host_state);
          }
          if (data.power_ups !== undefined) {
            setSyncedPowerUps(data.power_ups);
          }
        }
        break;
        
      case 'bullet_sync':
        // Sync bullets from other players
        if (data.wallet_address !== walletAddress) {
          setSyncedBullets(prev => [
            ...prev.filter(b => b.owner !== data.wallet_address),
            ...data.bullets.map(b => ({ ...b, owner: data.wallet_address }))
          ]);
        }
        break;
        
      case 'player_hit':
        // Another player hit an enemy
        // This is handled by the game engine
        break;
        
      case 'host_assigned':
        isHostRef.current = !!data.is_host;
        setIsHost(!!data.is_host);
        console.log('[MP] Host status:', data.is_host);
        break;
        
      case 'player_disconnected':
        // Remove disconnected player
        setOtherPlayers(prev => {
          const updated = { ...prev };
          delete updated[data.wallet_address];
          return updated;
        });
        break;
        
      default:
        // Ignore lobby messages during gameplay
        break;
    }
  }, [walletAddress]);
  
  // Handle game actions from other players
  const handleGameAction = useCallback((action, actionData, senderWallet) => {
    switch (action) {
      case 'shoot':
        // Add bullet from other player. Tag it with a unique id so the engine
        // can mark it rendered (avoiding double-spawn). Cap array at 200 to
        // prevent unbounded growth — older entries are pruned automatically.
        setSyncedBullets(prev => {
          const next = [
            ...prev,
            {
              ...actionData,
              id: `${senderWallet}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              owner: senderWallet,
              isRemote: true,
              spawnedAt: Date.now()
            }
          ];
          // Prune entries older than 5s OR keep the last 200 — whichever is tighter
          const cutoff = Date.now() - 5000;
          const filtered = next.filter(b => (b.spawnedAt || 0) > cutoff);
          return filtered.length > 200 ? filtered.slice(-200) : filtered;
        });
        break;

      case 'enemy_killed':
        // Peer killed an enemy — game engine reads syncedEnemyKills below
        setSyncedEnemyKills(prev => [
          ...prev.slice(-100),
          {
            ...actionData,
            owner: senderWallet,
            spawnedAt: Date.now()
          }
        ]);
        break;

      case 'credit_kill':
        // Host attributes a kill to me (the original shooter of a remote bullet).
        // Only apply if this credit_kill event is addressed to this client's wallet.
        if (actionData && actionData.shooter === walletAddress) {
          setPendingKillCredits(prev => [
            ...prev.slice(-200),
            { points: actionData.points || 0, scoreValue: actionData.scoreValue || 0, ts: Date.now() }
          ]);
        }
        break;

      case 'powerup_collected':
        // Peer collected a power-up: queue id for engine to remove from local list.
        if (actionData && actionData.id) {
          setPendingPowerupPickupIds(prev => [...prev.slice(-200), actionData.id]);
        }
        break;

      case 'revival_triggered':
        // Peer triggered Extra Life. Queue for engine to spawn a heart-particle
        // flourish above their on-screen position so we share the celebration.
        if (actionData) {
          setPendingRevivals(prev => [...prev.slice(-50), {
            wallet: senderWallet,
            x: actionData.x,
            y: actionData.y,
            ts: Date.now(),
          }]);
        }
        break;

      case 'grenade':
      default:
        break;
    }
  }, [walletAddress]);
  
  // Send local player state
  const sendPlayerState = useCallback((state) => {
    if (!subRef.current) return;
    localStateRef.current = state;
    subRef.current.send({
      type: 'player_state',
      state: {
        x: state.x,
        y: state.y,
        groundY: state.groundY,
        health: state.health,
        direction: state.direction,
        isJumping: state.isJumping,
        currentWeapon: state.currentWeapon,
        color: state.color,
        nftId: state.nftId,
        score: state.score,
        kills: state.kills
      }
    });
  }, []);
  
  // Send game action
  const sendGameAction = useCallback((action, data) => {
    if (!subRef.current) return;
    subRef.current.send({ type: 'game_action', action, data });
  }, []);
  
  // Send enemy sync (host only). Optional `hostState` mirrors authoritative
  // game-level fields (bossActive, levelProgress, level, bossDefeated) so guest
  // can keep its UI / triggers in lockstep instead of inferring from snapshot.
  // Optional `powerUps` is the host's authoritative power-up list.
  const sendEnemySync = useCallback((enemies, hostState = null, powerUps = null) => {
    if (!subRef.current) return;
    if (!isHostRef.current) return; // Only host syncs enemies
    subRef.current.send({
      type: 'enemy_sync',
      enemies: enemies.map(e => ({
        id: e.id,
        x: e.x,
        y: e.y,
        groundY: e.groundY,
        jumpZ: e.jumpZ,
        health: e.health,
        maxHealth: e.maxHealth,
        type: e.type,
        difficulty: e.difficulty,
        level: e.level,
        velocityX: e.velocityX,
        isDead: !!e.isDead,
        isBoss: !!e.isBoss,
        phase: e.phase || 1,
        animFrame: e.animFrame,
        bossName: e.bossName
      })),
      host_state: hostState,
      power_ups: powerUps ? powerUps.map(p => ({
        id: p.id,
        x: p.x,
        y: p.y,
        groundY: p.groundY,
        type: p.type,
        active: p.active !== false
      })) : null
    });
  }, []);

  // Drain pending kill credits — engine should call this once per frame to
  // collect any remote-attributed kills and apply them locally.
  const consumeKillCredits = useCallback(() => {
    if (pendingKillCredits.length === 0) return [];
    const drained = pendingKillCredits;
    setPendingKillCredits([]);
    return drained;
  }, [pendingKillCredits]);

  // Drain pending power-up pickup ids — engine drains and removes matching
  // power-ups from its local list (host removes from authoritative list).
  const consumePowerupPickups = useCallback(() => {
    if (pendingPowerupPickupIds.length === 0) return [];
    const drained = pendingPowerupPickupIds;
    setPendingPowerupPickupIds([]);
    return drained;
  }, [pendingPowerupPickupIds]);

  // Drain pending peer revival events — engine spawns heart-particle flourish
  // above the peer who triggered an Extra Life.
  const consumeRevivals = useCallback(() => {
    if (pendingRevivals.length === 0) return [];
    const drained = pendingRevivals;
    setPendingRevivals([]);
    return drained;
  }, [pendingRevivals]);
  
  // Send bullet sync
  const sendBulletSync = useCallback((bullets) => {
    if (!subRef.current) return;
    // Only send player's own bullets
    const ownBullets = bullets.filter(b => !b.isRemote && !b.isEnemy);
    subRef.current.send({
      type: 'bullet_sync',
      bullets: ownBullets.map(b => ({
        x: b.x,
        y: b.y,
        direction: b.direction,
        damage: b.damage,
        color: b.color
      }))
    });
  }, []);
  
  // Cleanup stale players
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setOtherPlayers(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(wallet => {
          if (now - updated[wallet].lastUpdate > 5000) {
            // Player hasn't sent update in 5 seconds
            delete updated[wallet];
          }
        });
        return updated;
      });
    }, 2000);
    
    return () => clearInterval(cleanupInterval);
  }, []);
  
  // (Note: connect lifecycle handled by the lobbySocket subscriber effect above.)
  
  return {
    isConnected,
    otherPlayers,
    syncedEnemies,
    syncedBullets,
    syncedEnemyKills,
    syncedHostState,
    syncedPowerUps,
    walletAddress,
    isHost,
    sendPlayerState,
    sendGameAction,
    sendEnemySync,
    sendBulletSync,
    consumeKillCredits,
    consumePowerupPickups,
    consumeRevivals
  };
};

export default useMultiplayerSync;
